import React, { useContext } from "react";
import { Box } from "@mui/material";

import { StackedNavigationContext, useStackedNavigationContext } from "./StackedNavigationContext";
import StackedNavigationHeader from "./StackedNavigationHeader";
import StackedNavigationProvider from "./StackedNavigationProvider";

import { StackablePanel } from "./StackedNavigation.styles";

type Screen = {
  id: string;
  name: string;
  component: React.ReactNode;
  useDefaultHeader?: boolean;
};

interface IProps {
  rootComponent: React.ReactNode;
  screens: Screen[];
}

const StackedNavigation: React.FC<IProps> = ({ rootComponent, screens }) => {
  // Check if we're already inside a provider
  const existingContext = useContext(StackedNavigationContext);
  const hasExternalProvider = existingContext !== undefined;

  // If no external provider, create our own (backward compatibility)
  if (!hasExternalProvider) {
    const screenIds = screens.map((screen) => screen.id);
    return (
      <StackedNavigationProvider screenIds={screenIds}>
        <StackedNavigationUI rootComponent={rootComponent} screens={screens} />
      </StackedNavigationProvider>
    );
  }

  // Use external provider
  return <StackedNavigationUI rootComponent={rootComponent} screens={screens} />;
};

const StackedNavigationUI: React.FC<IProps> = ({ rootComponent, screens }) => {
  const { stack, goBack } = useStackedNavigationContext();

  return (
    <Box position="relative" height="100%" overflow="hidden">
      {rootComponent}
      {stack.map((id) => {
        const targetScreenIndex = screens.findIndex((screen) => screen.id === id);
        if (targetScreenIndex === -1) return null;
        const { name, component, useDefaultHeader = true } = screens[targetScreenIndex];

        return (
          <StackablePanel data-testid={`stackable-panel-${id}`} key={id} className="customScrollbars" zIndex={1}>
            {useDefaultHeader && <StackedNavigationHeader onBackButtonClick={() => goBack(1)} title={name} />}
            {component}
          </StackablePanel>
        );
      })}
    </Box>
  );
};

export default StackedNavigation;
